import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, KeyRound, Send } from 'lucide-react'
import { useState } from 'react'

import { ErrorDeLaApi, pedir } from '../../api/nucleo'
import { Aviso, Boton, clases, claseDeEntrada } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { Envoltura } from './Controles'

/**
 * La dirección del webhook de un formulario (CLAUDE.md §9, `src/formularios/webhook.ts` en el núcleo): dónde se manda
 * cada respuesta, el secreto con el que se firma para comprobarla, «Probar» (un aviso de prueba firmado, con lo que
 * contestó el servidor) y cómo fueron las últimas entregas. Lo que se guarda es solo la dirección; el secreto lo da el
 * núcleo y nunca se escribe aquí.
 */
type Entrega = { respuesta: number; fecha: null | string; estado?: string; intentos?: number; codigo?: null | number; mensaje?: string }
type DatosDelWebhook = { secreto: string; ultimas: Entrega[] }

const ESTADO: Record<string, string> = { entregado: 'Entregado', reintentando: 'Reintentando', fallido: 'Falló' }

export const Webhook = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const id = typeof (valores as { id?: unknown }).id === 'number' ? ((valores as { id: number }).id as number) : null
  const cliente = useQueryClient()
  const clave = ['webhook-de-formulario', id]
  const { data } = useQuery({ queryKey: clave, enabled: id !== null, queryFn: () => pedir<DatosDelWebhook>(`/formularios/webhook?id=${id}`), retry: false })
  const [verSecreto, setVerSecreto] = useState(false)
  const [ocupado, setOcupado] = useState<null | 'probar' | 'secreto'>(null)
  const [aviso, setAviso] = useState<null | { tono: 'error' | 'ok'; texto: string }>(null)

  const probar = async () => {
    if (id === null) return
    setOcupado('probar')
    setAviso(null)
    try {
      const r = await pedir<{ ok: boolean; mensaje: string }>('/formularios/webhook/probar', { metodo: 'POST', cuerpo: { id, url: valor ?? '' } })
      setAviso({ tono: r.ok ? 'ok' : 'error', texto: r.ok ? `Prueba enviada: ${r.mensaje}` : r.mensaje })
    } catch (e) {
      setAviso({ tono: 'error', texto: e instanceof ErrorDeLaApi ? e.message : 'No se ha podido probar. Vuelve a intentarlo.' })
    } finally {
      setOcupado(null)
    }
  }

  const cambiarSecreto = async () => {
    if (id === null || !window.confirm('El secreto de ahora dejará de valer: tendrás que poner el nuevo en tu servidor. ¿Cambiarlo?')) return
    setOcupado('secreto')
    setAviso(null)
    try {
      await pedir('/formularios/webhook/secreto', { metodo: 'POST', cuerpo: { id } })
      await cliente.invalidateQueries({ queryKey: clave })
      setVerSecreto(true)
      setAviso({ tono: 'ok', texto: 'Secreto cambiado. Pon el nuevo en tu servidor.' })
    } catch (e) {
      setAviso({ tono: 'error', texto: e instanceof ErrorDeLaApi ? e.message : 'No se ha podido cambiar el secreto.' })
    } finally {
      setOcupado(null)
    }
  }

  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input
        aria-invalid={Boolean(error)}
        className={clases(claseDeEntrada(error), 'font-mono text-[13px]')}
        disabled={soloLectura}
        id={ruta}
        inputMode="url"
        onChange={(e) => cambiar(e.target.value)}
        placeholder={campo.placeholder ?? 'https://'}
        value={valor ?? ''}
      />
      {id === null ? (
        <p className="text-xs text-tenue">Guarda el formulario para ver el secreto y probar la dirección.</p>
      ) : (
        <div className="mt-2 flex flex-col gap-2 rounded-campo border border-linea bg-papel px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <KeyRound aria-hidden className="size-4 text-tenue" />
            <span className="font-medium">Secreto para comprobar la firma</span>
            {data && (
              <>
                <code className="rounded bg-papel-2 px-1.5 py-0.5 font-mono text-xs">{verSecreto ? data.secreto : `${data.secreto.slice(0, 10)}••••••••`}</code>
                <button className="text-violeta hover:underline" onClick={() => setVerSecreto(!verSecreto)} type="button">
                  {verSecreto ? 'Ocultar' : 'Ver'}
                </button>
                <button aria-label="Copiar el secreto" className="inline-flex items-center gap-1 text-violeta hover:underline" onClick={() => void navigator.clipboard?.writeText(data.secreto)} type="button">
                  <Copy aria-hidden className="size-3.5" /> Copiar
                </button>
              </>
            )}
          </div>
          <p className="text-xs text-tenue">
            Cada aviso lleva la cabecera <code>Webhook-Firma: t=…,v1=…</code>: <code>v1</code> es el HMAC-SHA256 en hexadecimal de «t.cuerpo» con este secreto. Compruébalo antes de fiarte del aviso.
          </p>
          {!soloLectura && (
            <div className="flex flex-wrap gap-2">
              <Boton cargando={ocupado === 'probar'} disabled={!valor?.trim()} icono={<Send />} onClick={() => void probar()} pequeno>
                Probar la dirección
              </Boton>
              <Boton cargando={ocupado === 'secreto'} onClick={() => void cambiarSecreto()} pequeno>
                Cambiar el secreto
              </Boton>
            </div>
          )}
          {aviso && <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>}
          {data && data.ultimas.length > 0 && (
            <ul className="flex flex-col gap-0.5 text-xs text-tenue">
              {data.ultimas.map((u) => (
                <li key={u.respuesta}>
                  Respuesta {u.respuesta}: {ESTADO[u.estado ?? ''] ?? u.estado}
                  {u.intentos && u.intentos > 1 ? ` (${u.intentos} intentos)` : ''} · {u.mensaje}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Envoltura>
  )
}
